import {TerritoryBaseLayer} from "@esWebAPI/es/map/layer/territory/TerritoryBaseLayer";
import {TerritoryPolygonRelatedLayer} from "@esWebAPI/es/map/layer/territory/TerritoryPolygonRelatedLayer";
import {ITerritoryLayer} from "@esWebAPI/es/map/layer/territory/TerritoryLayer";
import {TerritoryPointRelatedLayer} from "@esWebAPI/es/map/layer/territory/TerritoryPointRelatedLayer";
import {TerritoryOutlinesLayer} from "@esWebAPI/es/map/layer/territory/TerritoryOutlinesLayer";

import type TerritoryModel from "@esWebAPI/es/map/TerritoryModel";
import type {ITerritoryFeature} from "@esWebAPI/es/map/TerritoryModel";
import type {ILayerView} from "@esWebAPI/es/model/Layer";
import type {Feature} from "geojson";
import type {IMapLayer} from "@esWebAPI/es/map/layer/MapLayer";
import type { FeatureViewModel } from "../../FeatureViewModel";

export class TerritoryLayerUtils {

    public static isTerritoryLayer(layer: IMapLayer) {
        return layer instanceof TerritoryBaseLayer ||
            layer instanceof TerritoryPolygonRelatedLayer ||
            layer instanceof TerritoryPointRelatedLayer ||
            layer instanceof TerritoryOutlinesLayer;
    }

    /**
     *
     * @param feature
     * @param territoryLayer
     * @param ter territoryFeature that baseFeat is assigned to (see: #getByBaseFeatureId(), #getByBasePointFeatureId())
     * @returns {boolean}
     */
    private static filter(feature: any, territoryLayer: ITerritoryLayer, ter: ITerritoryFeature|null) {
        const territoryModel = territoryLayer.territoryModel;
        const filteredTerritoriesIds = territoryModel.getFilter();

        if (!filteredTerritoriesIds) {
            return false; // Filter is empty, feature is visible
        }

        if (territoryLayer instanceof TerritoryBaseLayer) {
            return ter && !filteredTerritoriesIds.includes(ter.ES_ID);
        }

        if (territoryLayer instanceof TerritoryPointRelatedLayer) {
            return ter && !filteredTerritoriesIds.includes(ter.ES_ID)
        }

        if (territoryLayer instanceof TerritoryOutlinesLayer){
            return feature && !filteredTerritoriesIds.includes(feature.properties.territoryId);
        }

        if (territoryLayer instanceof TerritoryPolygonRelatedLayer) {
            const layerView = territoryLayer.layerView;
            const mapping = territoryModel.getRelatedFeaturesMapping().getForLayer(layerView.id);

            if (mapping[feature.id] && mapping[feature.id].length > 0) {
                for(let i = 0; i < mapping[feature.id].length; i++) {
                    const baseFeatId = mapping[feature.id][i];
                    const territory = territoryModel.getByBaseFeatureId(baseFeatId);
                    if (!territory || filteredTerritoriesIds.includes(territory.ES_ID)) {
                        return false; // At least one base features of the related feature is visible
                    }
                }
                return true; // No base features of the related feature are visible
            }
        }

        return false;
    }

    /**
     * Check if the feature is visible at the current level
     * @param territoryLayer
     * @param feature
     * @param territory
     * @return {boolean}
     */
    public static isVisible(territoryLayer: ITerritoryLayer, feature: any, territory: ITerritoryFeature|null) {
        let ter: ITerritoryFeature|null = territory;
        const territoryModel = territoryLayer.territoryModel;
        const isHierarchical = territoryModel.isHierarchical();
        const level = territoryModel.getCurrentLevel()
        const levels = territoryModel.getTerritoryMetaObjRef().levels;

        // these methods called lots of times (isVisibleRes contains some logic so just cache) others just easier to read
        const isVisibleRes = territoryLayer.isVisibleRes();
        const isBaseLayer = territoryLayer instanceof TerritoryBaseLayer;
        const isRelatedLayer = territoryLayer instanceof TerritoryPolygonRelatedLayer;
        const isExtraPointLayer = territoryLayer instanceof  TerritoryPointRelatedLayer;
        const isOutlinesLayer = territoryLayer instanceof TerritoryOutlinesLayer;

        if (!ter && (isBaseLayer || isExtraPointLayer)) {
            ter = isBaseLayer ? territoryModel.getByBaseFeatureId(feature.id, level) :
                territoryModel.getByBasePointFeatureId(feature.id);
        }

        if (this.filter(feature, territoryLayer, ter)){
            return false;
        }
        // Bases features on non-hierarchical
        else if (!isHierarchical && (isVisibleRes || (ter && isBaseLayer))) {
            return true;
        }

        // Polygon related features are not visible on upper levels
        else if (isHierarchical && isRelatedLayer  && level && level < levels) {
            return false;
        }

        // Assigned bases features on hierarchical top levels
        else if (isHierarchical && !isExtraPointLayer && (isVisibleRes || isBaseLayer) && level && level < levels) {
            if (isRelatedLayer || (ter && isBaseLayer)) {
                return true;
            } else if (isOutlinesLayer){
                return true;
            }
        }
        // Assigned base features on hierarchical bottom level
        else if (ter && ter.ES_LEVEL && ter.ES_LEVEL === levels && territoryLayer instanceof TerritoryBaseLayer) {
            return true;
        }

        // Un-assigned base features are visible.
        else if (!ter && isBaseLayer && isVisibleRes) {
            return true
        }

        // Outlines at bottom level
        else if (isOutlinesLayer && level === levels){
            return false;
        }

        //Extra point feature
        else if(isExtraPointLayer){
            return !(isHierarchical && isVisibleRes && !ter && level && level < levels);
        }

        // Related features
        else if(isRelatedLayer && isVisibleRes) {
            return true;
        }

        return false;
    }

    /**
     *
     * @param territoryLayer
     * @param feature
     * @param territory
     * @returns {*|boolean}
     */
    public static isSelectable(territoryLayer: ITerritoryLayer, feature: any, territory: ITerritoryFeature|null = null) {
        let ter: ITerritoryFeature|null = territory;
        const territoryModel = territoryLayer.territoryModel;
        const level = territoryModel.getCurrentLevel();

        ter = ter || territoryModel.getByBaseFeatureId(feature.id, level);

        if (territoryLayer instanceof TerritoryPointRelatedLayer) {
            if(!territoryModel.isLowestLevel()) {
                return  false;
            }

            ter = territoryModel.getByBasePointFeatureId(feature.id);
            return this.filter(feature, territoryLayer, ter);
        }

        if (this.filter(feature, territoryLayer, ter)) {
            return false;
        }

        if (territoryLayer instanceof TerritoryOutlinesLayer) {
            return level === feature.properties.level - 1;
        }

        // Assignment by related features when base feature are not visible
        if (territoryLayer instanceof TerritoryBaseLayer && !territoryLayer.isVisibleRes()) {
            return true;
        }

        return this.isVisible(territoryLayer, feature, ter);
    }

    /**
     * Return the dataset LayerViews as an array.
     * @return {array}
     */
    public static getDatasetLayerViews(territoryModel: TerritoryModel, featureViewModel: FeatureViewModel): ILayerView[] {
        const isLayerViewSelectable = function (layerView: any) {

            return (layerView.geomType === "POINT" &&
                layerView.$type !== "MapScopeCustomPointLayerView" &&
                layerView.$type !== "MapScopeTerritoryCentersLayerView"  &&
                !territoryModel.isRelatedLayer(layerView.id) &&
                !territoryModel.isRelatedExtraPointLayer(layerView.id))
        }

        const layerModels = featureViewModel.listLayers();
        const datasetLayerViews: ILayerView[] = [];
        for (let i = 0; i < layerModels.length; i++) {
            let layerView = layerModels[i].getLayerView();
            if(isLayerViewSelectable(layerView)) {
                datasetLayerViews.push(layerView);
            }
        }

        return datasetLayerViews;
    };

    public static getAssignedTerritory = function(territoryLayer: ITerritoryLayer, feature: any): ITerritoryFeature|null {
        const territoryModel = territoryLayer.territoryModel;

        if (territoryLayer instanceof TerritoryOutlinesLayer) {
            let child =  territoryModel.get(feature.id || feature.properties.territoryId); // TODO: AD to review. feature can be from worker or a territory feature
            return child.ES_PARENT_ID ? territoryModel.get(child.ES_PARENT_ID) : null;
        }

        if (!(territoryLayer instanceof TerritoryBaseLayer) && !(territoryLayer instanceof TerritoryPointRelatedLayer)) {
            return null;
        }

        if (territoryLayer instanceof TerritoryBaseLayer) {
            return territoryModel.getByBaseFeatureId(feature.id);
        }

        return territoryModel.getByBasePointFeatureId(feature.id);
    };

    // todo: unused - remove
    /*
    public static visualCenter(geometry: any): number[] {
        if (geometry.type === "Polygon") {
            return polylabel(geometry.coordinates).splice(0,2);
        }

        if (geometry.type === "MultiPolygon") {
            let widestGeometry = geometry.coordinates[0];
            for (let geom of geometry.coordinates) {
                if (data.BoundingBox.getWidth(data.BoundingBox.fromData(new data.Polygon(geom))) >
                    data.BoundingBox.getWidth(data.BoundingBox.fromData(new data.Polygon(widestGeometry)))) {
                    widestGeometry = geom;
                }
            }

            return polylabel(widestGeometry).splice(0,2);
        }

        throw new Error("Unsupported geometry for polylabel: " + geometry.type);
    }
    */

    public static createLabel(layerView: ILayerView, feature: Feature): string {
        let label = '';
        if (layerView.styleSettings.labelSettings.labelAttrs.length > 0) {
            const labelAttrs = layerView.styleSettings.labelSettings.labelAttrs as string[];
            const labelSeparator = layerView.styleSettings.labelSettings.labelSeparator as string;
            let isFirstAddedToLabel = true;
            labelAttrs.forEach((attr) => {
                const property = feature.properties?.[attr];
                if (property != null) { // feature may not contain attribute requested in label, add what is available only
                    if (isFirstAddedToLabel) {
                        isFirstAddedToLabel = false;
                        label += property;
                    } else {
                        label += labelSeparator;
                        label += property;
                    }
                }
            });
        } else {
            label = '';
        }

        return label;
    }
}
